import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { fromJS } from "immutable";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import LabelsSelect from "../../components/LabelsSelect";
import CustomField from "../CustomField";
import BucketSelect from "../../components/BucketSelect";
import AssigneesSelect from "../../components/AssigneesSelect";
import NumericFormatFieldDebounce from "../../components/ui/NumericFormatFieldDebounce";

const ProductForm = ({
  projectId,
  visibleFieldIds,
  isEditMode: _isEditMode,
  taskData,
  onDataChange,
  validationErrors,
}) => {
  const { t } = useTranslation();

  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";

  const customFields = fields.filter(
    (field: any) => field.get("isCustomField") && field.get("formType") == "PRODUCT"
  );

  const getTaskFieldValue = (fieldId: string) => {
    return taskData.getIn([fieldId, "value"]);
  };

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const BUILD_FIELDS: Record<string, any> = {
    name: (
      <div>
        <InputFieldDebounce
          required
          testId="product-form-name-input"
          name="name"
          label={t("Product Name")}
          placeholder={t("Set product name...")}
          value={getTaskFieldValue("name")}
          onChange={(value) => handleChangeField("name", value)}
          errorMessage={validationErrors.name}
        />
      </div>
    ),
    productCode: (
      <div>
        <InputFieldDebounce
          required
          testId="product-form-code-input"
          name="productCode"
          label={t("Product Code")}
          placeholder={t("Set product code...")}
          value={getTaskFieldValue("productCode")}
          onChange={(value) => handleChangeField("productCode", value)}
          errorMessage={validationErrors.productCode}
        />
      </div>
    ),
    unit: (
      <div>
        <InputFieldDebounce
          testId="product-form-unit-input"
          label={t("Unit")}
          placeholder={t("Set unit...")}
          value={getTaskFieldValue("unit")}
          onChange={(value) => handleChangeField("unit", value)}
          errorMessage={validationErrors.unit}
        />
      </div>
    ),
    price: (
      <div>
        <NumericFormatFieldDebounce
          required
          testId="product-form-price-input"
          name="price"
          label={t("Price")}
          value={getTaskFieldValue("price")}
          onChange={(value: string) => {
            handleChangeField("price", value);
          }}
          placeholder={t("Set a value")}
          errorMessage={validationErrors.price}
          currency={currency}
        />
      </div>
    ),
    unitPrice: (
      <div>
        <NumericFormatFieldDebounce
          required
          testId="product-form-unit-price-input"
          name="unitPrice"
          label={t("Unit price")}
          value={getTaskFieldValue("unitPrice")}
          onChange={(value: string) => {
            handleChangeField("unitPrice", value);
          }}
          placeholder={t("Set a value")}
          errorMessage={validationErrors.unitPrice}
          currency={currency}
        />
      </div>
    ),
    tax: (
      <div>
        <NumericFormatFieldDebounce
          testId="product-form-tax-input"
          label={t("Tax %")}
          value={getTaskFieldValue("tax")}
          onChange={(value: string) => {
            handleChangeField("tax", value);
          }}
          placeholder={t("Set a value")}
          errorMessage={validationErrors.tax}
          isPercentage={true}
          min={0}
          max={100}
        />
      </div>
    ),
    description: (
      <div>
        <InputFieldDebounce
          testId="product-form-description-input"
          mode="textarea"
          label={t("Description")}
          placeholder={t("Set description...")}
          value={getTaskFieldValue("description")}
          onChange={(value) => handleChangeField("description", value)}
          errorMessage={validationErrors.description}
        />
      </div>
    ),
    assignees: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Owners")}</label>
        <AssigneesSelect
          type="FORM"
          value={getTaskFieldValue("assignees")?.toJS() || []}
          onChange={(assigneeIds: string[]) => handleChangeField("assignees", assigneeIds)}
          placeholder={t("Select owners")}
        />
      </div>
    ),
    labels: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Labels")}</label>
        <LabelsSelect
          projectId={projectId}
          contentType="PRODUCT"
          type="FORM"
          value={getTaskFieldValue("labels")?.toJS() || []}
          onChange={(labelIds: string[]) => handleChangeField("labels", labelIds)}
          placeholder={t("Select labels")}
          showRemove
        />
      </div>
    ),
    bucketId: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Group")}</label>
        <BucketSelect
          projectId={projectId}
          contentType="PRODUCT"
          type="FORM"
          value={getTaskFieldValue("bucketId")}
          onChange={(bucketId: string) => handleChangeField("bucketId", bucketId)}
          placeholder={t("Select group")}
          isClearable
        />
        {validationErrors.bucketId && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.bucketId}</p>
        )}
      </div>
    ),
  };

  return (
    <div className="space-y-4">
      {visibleFieldIds.map((visibleFieldId) => {
        const customField = customFields.find((field) => field.get("id") === visibleFieldId);
        if (customField && customField.get("id")) {
          const fieldData = {
            ...customField.toJS(),
            value: getTaskFieldValue(customField.get("id")),
          };
          return (
            <div className="w-full" key={`${customField.get("id")}`}>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                {t(customField.get("label"))}
              </label>
              <CustomField
                field={fieldData}
                onChange={(value: any) => {
                  handleChangeField(customField.get("id"), value);
                }}
              />
            </div>
          );
        }
        return <React.Fragment key={visibleFieldId}>{BUILD_FIELDS[visibleFieldId]}</React.Fragment>;
      })}
    </div>
  );
};

export default ProductForm;
